"use client";

import React, { memo, useState } from "react";
import { ChevronsUpDownIcon, Filter, Pencil } from "lucide-react"; // 👈 Added StarIcon for demo
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import EllipsisTooltip from "@component/tooltip/EllipsisTooltip";

/**
 * Reusable Combobox Component
 *
 * @param {Array<string | { [key: string]: any }>} options
 * @param {string | object | Array} value - Selected value(s)
 * @param {(value: any) => void} onChange
 * @param {string} placeholder
 * @param {string} width
 * @param {boolean} multiselect
 */
const ComboBox = ({
  id = "value",
  name = "label",
  options = [],
  value,
  onChange,
  placeholder = "Select option...",
  disabled = false,
  width = "w-full",
  multiselect = false,
  isVisibleValue = true,
  errorBorders = "",
  isLoading,
  selectingLimit = 0,
  disabledPortal = true,
  onBlur = () => null,
  contentClassName = '',
  disabledAccessKey = '',
  buttonClassName = '',
  type = 'input',
  align = 'start',
  withTag = false,
  containerRef,
  iconConditionKey = "",
  ContionIcon = Pencil,
  NeedToCheckKey,
  iconHoverTitle = "You can edit this rate card",
  emptyText='No data found',
}) => {
  const [open, setOpen] = useState(false);
  const isObjectOptions = typeof options?.[0] === "object";

  const handleCondition = (item, key) => {
    return isObjectOptions && typeof item === "object" && typeof options === "object"
      ? item?.[key]
      : typeof item === "string" && !isObjectOptions
        ? options?.find((v) => v === item)
        : options?.find((v) => v?.[id] === item)?.[key];
  };

  const getItemId = (item) => handleCondition(item, id);
  const getItemLabel = (item) => handleCondition(item, name);

  const isDisabledItem = (item) => {
    return typeof item === "object" && disabledAccessKey
      ? Boolean(handleCondition(item, disabledAccessKey))
      : false;
  };

  const isSelected = (item) => {
    if (multiselect) {
      return Array.isArray(value) && value.some((v) => getItemId(v) === getItemId(item));
    }
    return getItemId(value) === getItemId(item);
  };

  const handleSelect = (item) => {
    if (multiselect) {
      let newValue = Array.isArray(value) ? [...value] : [];
      const exists = newValue.find((v) => getItemId(v) === getItemId(item));
      if (exists) {
        newValue = newValue.filter((v) => getItemId(v) !== getItemId(item));
      } else {
        if (newValue.length >= selectingLimit && selectingLimit) return;
        newValue.push(item);
      }
      onChange(newValue);
    } else {
      onChange(item);
      setOpen(false);
    }
  };

  const displayValue = () => {
    if (multiselect) {
      return Array.isArray(value) && value.length > 0
        ? value.map((v) => getItemLabel(v)).join(", ")
        : placeholder;
    }
    return getItemLabel(typeof value === "string" ? value : value?.[0] || value) || placeholder;
  };

  // const shouldShowIcon = (item) => {
  //   if (!iconConditionKey) return false;

  //   const value = item?.[iconConditionKey];
  //   // If NeedToCheckKey is provided, match that value (supports "Y"/"N"/true/false)
  //   if (NeedToCheckKey !== undefined && NeedToCheckKey !== null && NeedToCheckKey !== "") {
  //     return value === NeedToCheckKey;
  //   }

  //   // Default fallback: show when true or "Y"
  //   return value === true || value === "Y";
  // };

  const shouldShowIcon = (item) => {
    if (!iconConditionKey) return false;

    // ✅ If NeedToCheckKey is an object, all conditions must match
    if (NeedToCheckKey && typeof NeedToCheckKey === "object") {
      return Object.entries(NeedToCheckKey).every(
        ([key, expected]) => item?.[key] === expected
      );
    }

    // ✅ If NeedToCheckKey is a string or boolean
    if (typeof NeedToCheckKey === "string" || typeof NeedToCheckKey === "boolean") {
      const value = item?.[iconConditionKey];
      return value === NeedToCheckKey;
    }

    // ✅ Default fallback
    const value = item?.[iconConditionKey];
    return value === true || value === "Y";
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger disabled={disabled} asChild={!disabled} className="w-full">
        {type === "button" ? (
          <Button
            startIcon={<Filter />}
            className="!bg-white text-form-label dark:!bg-muted shadow-none border border-form-border h-full"
          >
            Filter
          </Button>
        ) : (
          <div
            role="combobox"
            aria-expanded={open}
            aria-controls="combobox-options-list"
            onClick={() => !disabled && setOpen(!open)}
            className={cn(
              width,
              "font-sans flex h-10 items-center border border-border justify-between rounded-lg bg-primary-foreground px-3 py-1 text-base text-left transition-colors placeholder-[#999] focus:outline-none focus:ring-0 focus:border-[#e1e9f0] cursor-pointer text-[14px]",
              !disabled ? "" : " cursor-not-allowed",
              buttonClassName,
              errorBorders && " border-red-500",
            )}
          >
            <span
              className={cn(
                "font-sans flex-1 break-words line-clamp-1 text-foreground",
                !(
                  (multiselect && Array.isArray(value) && value.length > 0) ||
                  getItemLabel(value)
                ) && "text-muted-foreground"
              )}
              title={displayValue()}
            >
              {isLoading ? "Loading..." : isVisibleValue ? displayValue() : placeholder}
            </span>
            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </div>
        )}
      </PopoverTrigger>

      <PopoverContent
        disabledPortal={disabledPortal}
        align={align}
        sideOffset={4}
        containerRef={containerRef}  // attach to container}
        // className={cn(
        //   "font-sans p-0 w-[var(--radix-popover-trigger-width)] max-h-[250px] rounded-lg border border-gray-300 bg-white shadow-sm",
        //   "before:absolute before:inset-0 before:border before:border-gray-300 before:rounded-lg" // ensure full border
        // )}
        className={cn(
          "w-[var(--radix-popover-trigger-width)] p-0 pb-4 bg-primary-foreground",
          contentClassName
        )}
        onBlur={onBlur}
      >
        <Command avoidCollisions={false}>
          <CommandInput placeholder="Search..." />
          <CommandList id="combobox-options-list" className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="w-8 h-8 rounded-full border-2 border-border border-t-blue-600 animate-spin mb-2"></div>
                <span className="text-sm text-muted-foreground">Loading options ...</span>
              </div>
            ) : (
              <>
              <CommandEmpty>{emptyText}</CommandEmpty>
                {Array.isArray(options) && options[0]?.group
                  ? // ✅ Render grouped sections
                  options.map((group, idx) => (
                    <CommandGroup key={idx} heading={
                      <span className="text-[14px] font-bold text-foreground">
                        {group.group}
                      </span>
                    }>
                      {group.items.map((item) => (
                        <CommandItem
                          key={item.id}
                          value={item.name}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelect(item);
                          }}
                          className="text-[14px] flex justify-between items-center"
                        >
                          <span>{item.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))
                  : // ✅ Render flat list if not grouped
                  <CommandGroup>
                    {options.map((item) => (
                      <CommandItem
                        key={getItemId(item)}
                        value={getItemLabel(item)}
                        disabled={isDisabledItem(item)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelect(item);
                        }}
                        className="text-[14px] flex justify-between items-center"
                      >
                        {multiselect ? (
                          <div
                            className={`flex items-center ${isDisabledItem(item) && "opacity-50"
                              }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected(item)}
                              readOnly
                              className="mr-2 accent-blue-500"
                            />
                            <span>{getItemLabel(item)}</span>
                          </div>
                        ) : (
                          <span className={`${isDisabledItem(item) && "opacity-50"}`}>
                            {getItemLabel(item)}
                          </span>
                        )}

                        {shouldShowIcon(item) && (
                          <EllipsisTooltip title={iconHoverTitle}>
                            <ContionIcon className="h-4 w-4 ml-2 shrink-0" />
                          </EllipsisTooltip>
                        )}

                      </CommandItem>
                    ))}
                  </CommandGroup>}
              </>
            )}
          </CommandList>

        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default memo(ComboBox);
